import React from "react";
import { Tag, Typography } from "@pankod/refine-antd";

import {
  IThematicEventLevel,
  ITriviaLevelConfig,
  IJigsortLevelConfig,
} from "interfaces";

const { Text } = Typography;

/** First value of a TranslatedString in insertion order, or empty string. */
export const firstLangValue = (
  ts?: Record<string, string>
): string => {
  if (!ts) return "";
  const entries = Object.entries(ts);
  return entries.length > 0 ? entries[0][1] : "";
};

/**
 * Compact one-line preview of a level's config, dispatched by level_type.
 * Used by both the collapsed grid cards in the editor (live, reading form
 * state via Form.useWatch) and by the show page's levels table.
 *
 * Accepts a Partial so it can render brand-new levels whose level_type / config
 * may not be set yet — falls back to a placeholder.
 */
export const renderLevelSummary = (
  level: Partial<IThematicEventLevel>
): React.ReactNode => {
  if (!level.level_type) {
    return <Text type="secondary">No config yet</Text>;
  }

  if (level.level_type === "trivia") {
    const c = (level.config ?? {}) as ITriviaLevelConfig;
    if (c.trivia_id) {
      return <Text code>trivia_id: {c.trivia_id}</Text>;
    }
    return (
      <Text>
        level: {c.level ?? "-"}
        {c.keywords && c.keywords.length > 0 && (
          <>
            {" "}
            &nbsp;&nbsp; keywords:{" "}
            {c.keywords.map((kw) => (
              <Tag key={kw}>{kw}</Tag>
            ))}
          </>
        )}
      </Text>
    );
  }

  if (level.level_type === "jigsort") {
    const c = (level.config ?? {}) as IJigsortLevelConfig;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {c.image?.url && (
          <img
            src={c.image.url}
            alt=""
            style={{
              width: 40,
              height: 40,
              objectFit: "cover",
              borderRadius: 4,
              border: "1px solid #eee",
              flex: "0 0 auto",
            }}
          />
        )}
        <Text>
          difficulty: {c.difficulty ?? "-"}
          {firstLangValue(c.title) && (
            <> &nbsp;&nbsp; "{firstLangValue(c.title)}"</>
          )}
        </Text>
      </div>
    );
  }

  // minigame: free-form JSON preview, single line truncated with ellipsis so
  // long configs don't blow out the card height in the grid. We use raw CSS
  // (not antd's <Text ellipsis>) because the latter wasn't constraining width
  // reliably inside the Card body.
  return (
    <div
      title={JSON.stringify(level.config)}
      style={{
        fontSize: 12,
        fontFamily: "monospace",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "100%",
      }}
    >
      {JSON.stringify(level.config)}
    </div>
  );
};
